import {
	PresetData,
	ResolvedPresetsRefArray,
	ResolvedPresetsVariantRelation,
	ResolvedRefArray,
	ResolvedVariantRelation,
	UnresolvedRefArray,
	UnresolvedVariantRelation,
} from '@wix/thunderbolt-becky-types'
import _ from 'lodash'
import { REF_DELIMITER, removeHash } from './utils'
import {
	getDataOverrides,
	getOverridesRefArrayId,
	inflateResolvedRefArray,
	resolveRefArray,
	StyleOrLayoutItem,
	StyleOrLayoutMap,
} from './inflationUtils'

// "ref1_r_ref2_compId-query => ["ref2_compId-query", "ref1_r_ref2_compId-query"]
const getQueries = (dataQuery: string) => {
	const res: Array<string> = []
	_(dataQuery)
		.split(REF_DELIMITER)
		.reverse()
		.forEach((refId, index) => {
			if (index > 0) {
				res.push(`${refId}${REF_DELIMITER}${res[index - 1]}`)
			} else {
				res.push(refId)
			}
		})
	res.shift()
	return res
}

// Returns the item under the preset condition.
// If there is no item under the preset condition, returns the first item which isn't not under any condition.
const getItemUnderPresetCondition = (
	presetCondition: string,
	refArray: ResolvedRefArray<StyleOrLayoutItem>
): StyleOrLayoutItem => {
	const getDefaultItem = () => refArray.values.find((value) => value.type !== 'VariantRelation') as StyleOrLayoutItem // there should always be a default value.

	const variantRelationWithPresetCondition = refArray.values.find(
		(value) =>
			value.type === 'VariantRelation' &&
			value.variants.some((variantId) => removeHash(variantId) === presetCondition)
	) as ResolvedVariantRelation<StyleOrLayoutItem>
	return variantRelationWithPresetCondition ? variantRelationWithPresetCondition.to : getDefaultItem()
}

const resolveItemsUnderPreset = (
	presetDataItem: PresetData | ResolvedPresetsVariantRelation,
	presetItemKey: 'layout' | 'style',
	resolvedRefArray: ResolvedRefArray<StyleOrLayoutItem>,
	dataQuery: string,
	mergedDataMap: StyleOrLayoutMap
): ResolvedRefArray<StyleOrLayoutItem>['values'][0] => {
	if (presetDataItem.type === 'PresetData') {
		// Get the item under the specific preset and make in as default.
		const item = getItemUnderPresetCondition(presetDataItem[presetItemKey], resolvedRefArray)
		const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${presetItemKey}`
		const itemOverride = mergedDataMap[inflatedItemId] as StyleOrLayoutItem
		return itemOverride
			? itemOverride
			: {
					...item,
					id: inflatedItemId,
			  }
	}

	const presetItem = presetDataItem.to
	const inflatedVariantRelationId = `${dataQuery}-${presetDataItem.id}`
	const item = getItemUnderPresetCondition(presetItem[presetItemKey], resolvedRefArray)
	const inflatedItemId = `${dataQuery}-${presetDataItem.id}-${presetItemKey}`
	const variantRelationOverride = mergedDataMap[inflatedVariantRelationId] as UnresolvedVariantRelation
	if (variantRelationOverride) {
		// If we have override for the variant relation we should use it and just inflate the IDs in order
		// to be able to use the same widget more than one time in the same page
		const itemOverride = mergedDataMap[removeHash(variantRelationOverride.to)] as StyleOrLayoutItem
		// We resolve the "to" in order to be consistent with the flow when there is no override
		return {
			...variantRelationOverride,
			to: itemOverride,
		}
	}

	// Get the item under the specific preset and take the condition from the presetData
	const itemOverride = mergedDataMap[inflatedItemId] as StyleOrLayoutItem
	return {
		...presetDataItem,
		id: inflatedVariantRelationId,
		to: itemOverride
			? itemOverride
			: {
					...item,
					id: inflatedItemId,
			  },
	}
}

export const inflateWithPresets = (
	resolvedRefArray: ResolvedRefArray<StyleOrLayoutItem>,
	dataQuery: string,
	presets: Array<ResolvedPresetsRefArray['values']>,
	mapName: 'layout_data' | 'theme_data',
	mergedDataMap: StyleOrLayoutMap
) => {
	const presetItemKey = mapName === 'layout_data' ? 'layout' : 'style'
	const queries = getQueries(dataQuery)

	const orderedPresets = _.reverse([...presets])

	orderedPresets.forEach((presetsArray, index) => {
		dataQuery = queries[index]
		if (presetsArray.length) {
			resolvedRefArray.values = presetsArray.map((presetDataItem) =>
				resolveItemsUnderPreset(presetDataItem, presetItemKey, resolvedRefArray, dataQuery, mergedDataMap)
			)
		}
		const overridesRefArray = mergedDataMap[getOverridesRefArrayId(dataQuery)] as UnresolvedRefArray
		if (overridesRefArray) {
			const resolvedOverridesRefArray = resolveRefArray(overridesRefArray, mergedDataMap)
			const inflatedAndResolvedOverridesRefArray = inflateResolvedRefArray(resolvedOverridesRefArray, dataQuery)

			resolvedRefArray.values = resolvedRefArray.values.concat(inflatedAndResolvedOverridesRefArray.values)
		}
	})

	return getDataOverrides(resolvedRefArray, dataQuery)
}
